
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AppSettingsService, LogoHistory } from '@/services/app-settings-service';
import { useToast } from '@/hooks/use-toast';

export function useLogoSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get current logo URL
  const { data: currentLogoUrl, isLoading: isLoadingLogo } = useQuery({
    queryKey: ['logo-url'],
    queryFn: () => AppSettingsService.getCurrentLogoUrl(),
  });

  // Get logo history
  const { data: logoHistory = [], isLoading: isLoadingHistory } = useQuery({
    queryKey: ['logo-history'],
    queryFn: () => AppSettingsService.getLogoHistory(20),
  });

  // Update logo mutation
  const updateLogoMutation = useMutation({
    mutationFn: (logoUrl: string) => AppSettingsService.updateLogoUrl(logoUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['logo-url'] });
      queryClient.invalidateQueries({ queryKey: ['logo-history'] });
      toast({
        title: "Logo Updated Successfully",
        description: "The logo has been saved and applied globally.",
      });
    },
    onError: (error) => {
      console.error('Logo update failed:', error);
      toast({
        title: "Update Failed",
        description: "Failed to save logo. Please try again.",
        variant: "destructive"
      });
    },
  });

  return {
    currentLogoUrl,
    logoHistory,
    isLoading: isLoadingLogo || isLoadingHistory,
    updateLogo: updateLogoMutation.mutate,
    isUpdating: updateLogoMutation.isPending,
  };
}
